/**
 * The words public and assistant copy use for what Smart Manager costs to
 * start. New accounts and invited members get no free trial
 * (`NEW_ACCOUNT_TRIAL_ENABLED`, `INVITED_MEMBER_TRIAL_ENABLED`); they choose
 * a plan at sign-in, and a first subscription gets the pricebook's
 * intro offer. Every number comes from the pricebook, so ending or changing
 * the offer there changes this copy too. Pure, for the client bundle, the
 * server and the AI prompts.
 */

import type { Translate } from "@smartmanager/shell-ui/shared/i18n/translate";
import { CHECKOUT_PLAN_KEYS, checkoutPlanIntro, isCheckoutPlan } from "@/src/features/billing/checkoutPlans";
import { BILLING_PRICE_CENTS, formatUsdCents } from "@/src/features/billing/planPricebook";
import { BILLING_PLAN_KEYS, BILLING_PLAN_NAMES } from "@/src/features/billing/plans";

/**
 * "$45" a month for `months` months, then `regularAmount`, on `plan`;
 * `percentOff` is that discount, rounded down.
 */
export type PublicIntroOffer = {
  plan: string;
  amount: string;
  months: number;
  regularAmount: string;
  percentOff: number;
};

/**
 * The intro offer public copy advertises: the first plan on sale with a
 * monthly offer, or null when none is on sale. A visitor has never
 * subscribed, so it is the offer their first checkout gets.
 */
export const PUBLIC_INTRO_OFFER: PublicIntroOffer | null = (() => {
  for (const plan of CHECKOUT_PLAN_KEYS) {
    const intro = checkoutPlanIntro(plan, "monthly", true);
    if (intro) return { plan: BILLING_PLAN_NAMES[plan], ...intro };
  }
  return null;
})();

/**
 * Message key for the short offer line, filled with `PUBLIC_INTRO_OFFER`.
 * Owner 2026-10-22: the discount, not the price, because "$45/month for 3
 * months" read as $45 for all three.
 */
export const INTRO_OFFER_SHORT = "{percent}% off your first {months} months";

/** The short offer line ("43% off your first 3 months") for `offer`. */
export function introOfferTerms(t: Translate, offer: PublicIntroOffer): string {
  return t(INTRO_OFFER_SHORT, { percent: offer.percentOff, months: offer.months });
}

/**
 * The short offer line under Get started, or undefined when no offer is on
 * sale, so the line goes rather than promising something checkout won't give.
 */
export function introOfferNote(t: Translate): string | undefined {
  return PUBLIC_INTRO_OFFER ? introOfferTerms(t, PUBLIC_INTRO_OFFER) : undefined;
}

/**
 * The landing's Pricing tab, the desktop's and the phone's alike: the intro
 * offer shows once, on Pricing, where its terms are, and a screen reader hears
 * it with the tab's name.
 */
export function introOfferPricingTab(t: Translate): { label: string; offer?: string; ariaLabel?: string } {
  const label = t("Pricing");
  const offer = introOfferNote(t);
  return offer ? { label, offer, ariaLabel: `${label}, ${offer}` } : { label };
}

/**
 * The assistant's answer to "How much does it cost?": no trial, then each
 * plan on sale at its regular prices, the intro offer qualified to first
 * subscriptions (a signed-in customer who subscribed before pays the regular
 * price), and the plans not on sale yet.
 */
export function pricingFaqAnswer(): string {
  const plans = CHECKOUT_PLAN_KEYS.map((plan) => {
    const { monthly, yearly } = BILLING_PRICE_CENTS[plan];
    const intro = checkoutPlanIntro(plan, "monthly", true);
    const regular = `${BILLING_PLAN_NAMES[plan]} is ${formatUsdCents(monthly)} a month, or ${formatUsdCents(yearly)} a year.`;
    return intro
      ? `${regular} A first ${BILLING_PLAN_NAMES[plan]} subscription is ${intro.amount} a month for ${intro.months} months, then ${intro.regularAmount} a month; accounts that subscribed before pay the regular price.`
      : regular;
  });
  const comingSoon = BILLING_PLAN_KEYS.filter((plan) => !isCheckoutPlan(plan))
    .map((plan) => `${BILLING_PLAN_NAMES[plan]} is coming soon.`);
  return [
    "There is no free trial or free tier. Everyone chooses a plan when they sign up, including people a company invites.",
    ...plans,
    ...comingSoon,
    "Taxes may apply.",
  ].join(" ");
}
